import { useCallback, useEffect, useRef, useState } from "react";

// Generic localStorage-backed query hook. The default fetch pattern
// in this app used to be "boot → useEffect → supabase.select(...) →
// setState". Multiplied across 10+ hooks firing per session, that
// was ~1-2 MB of egress per page load PER USER for data that
// changes slowly. This helper inverts the default:
//
//   1. Hydrate state from localStorage instantly (zero network).
//   2. Skip the network fetch entirely when cache is fresh (TTL).
//   3. Refetch on TTL expiry or explicit `refresh()` call.
//   4. Realtime / mutation paths can call `mutate(updater)` to
//      inject local writes without round-tripping the DB.
//
// Trade-off: stale data within the TTL window. Pick the TTL based
// on how stale is acceptable for the domain — slow-changing data
// (profile, relationships, recipes) takes a 24h TTL; faster data
// (activity feed) takes minutes. CLAUDE.md "Supabase egress
// discipline" section is the load-bearing rule here.
//
// Usage:
//
//   const { data, loading, refresh, mutate } = useCachedQuery({
//     cacheKey: "myFeature.v1",
//     ttlMs: 24 * 60 * 60 * 1000,         // 24h
//     fetcher: async () => {
//       const { data } = await supabase
//         .from("my_table")
//         .select("*")
//         .eq("user_id", userId);
//       return data || [];
//     },
//     // Optional — short-circuit when fetcher requires inputs not
//     // yet available (e.g. userId still loading).
//     enabled: Boolean(userId),
//   });

export function useCachedQuery({
  cacheKey,
  ttlMs,
  fetcher,
  enabled = true,
  // Optional default to use when cache is empty AND enabled is
  // false — typically [] for list queries, null for singletons.
  defaultValue = null,
}) {
  const cached = readCache(cacheKey, ttlMs);
  const [data, setData] = useState(() =>
    cached !== null ? cached : defaultValue,
  );
  const [loading, setLoading] = useState(() =>
    enabled && cached === null,
  );

  const fetcherRef = useRef(fetcher);
  useEffect(() => { fetcherRef.current = fetcher; }, [fetcher]);

  const doFetch = useCallback(async () => {
    if (!enabled) return;
    try {
      const fresh = await fetcherRef.current();
      if (fresh === undefined) return;
      setData(fresh);
      writeCache(cacheKey, fresh);
    } catch (err) {
      console.warn(`[useCachedQuery:${cacheKey}] fetch failed:`, err?.message || err);
    } finally {
      setLoading(false);
    }
  }, [cacheKey, enabled]);

  // Boot effect — only network-fetch when cache is stale or absent.
  // Fresh cache hits short-circuit instantly; the user sees data
  // immediately and we skip the egress entirely.
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (cacheFresh(cacheKey, ttlMs)) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      await doFetch();
      if (!alive) return;
    })();
    return () => { alive = false; };
  }, [cacheKey, ttlMs, enabled, doFetch]);

  // Force a fresh network fetch + cache rewrite. Use sparingly —
  // every call is egress. Right home for refresh: post-mutation
  // sync, pull-to-refresh gestures, "I just changed something"
  // moments.
  const refresh = useCallback(async () => {
    await doFetch();
  }, [doFetch]);

  // Optimistic local mutation — apply a function/value to the cached
  // data and persist the new shape to localStorage. No network.
  // Pair with refresh() if you also need to durably commit to DB
  // (the caller does the supabase.update separately).
  const mutate = useCallback((updater) => {
    setData((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      writeCache(cacheKey, next);
      return next;
    });
  }, [cacheKey]);

  return { data, loading, refresh, mutate };
}

// ── localStorage helpers ──────────────────────────────────────────

function fullKey(cacheKey) {
  return `mise:cachedQuery:${cacheKey}`;
}

export function readCache(cacheKey, ttlMs) {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage?.getItem(fullKey(cacheKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.fetchedAt !== "number") return null;
    if (Date.now() - parsed.fetchedAt > ttlMs) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

export function writeCache(cacheKey, data) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage?.setItem(
      fullKey(cacheKey),
      JSON.stringify({ fetchedAt: Date.now(), data }),
    );
  } catch {
    // Quota / private mode — non-fatal; we'll re-fetch next boot.
  }
}

export function cacheFresh(cacheKey, ttlMs) {
  try {
    if (typeof window === "undefined") return false;
    const raw = window.localStorage?.getItem(fullKey(cacheKey));
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.fetchedAt !== "number") return false;
    return Date.now() - parsed.fetchedAt <= ttlMs;
  } catch {
    return false;
  }
}

// Bust a specific cache key — useful after a write the cache should
// reflect on next read. Most call sites should prefer mutate() over
// invalidate() since mutate is zero-network; invalidate forces a
// refetch on next mount which IS egress.
export function invalidateCache(cacheKey) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage?.removeItem(fullKey(cacheKey));
  } catch { /* noop */ }
}
